import { queryClient } from "@/src/config/react-query";
import {
  GroupMemberRole,
  JoinType,
  MembershipStatus,
} from "@/src/constants/groups/group-membership";
import { GroupEditProps } from "@/src/models/group.model";
import { InvitableMember, UserProfile } from "@/src/models/user.model";
import { callGroups, groupsFunctionEnabled, type GroupSnapshot } from "@/src/services/api/groups/groups-function";
import {
  useCreateGroup,
  useCreateGroupMembership,
  useUpdateGroup,
  useUpdateGroupMemberCount,
} from "@/src/services/api/groups/groups.hooks";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import { pickSingleWithCrop } from "@/src/utils/picker-utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUploadFile } from "../use-upload-file";

type GroupImageSelection =
  | { uri: string; fileName: string; mimeType: string; isLocal: boolean }
  | undefined;

/** Wraps an already-uploaded URL so it renders without being re-uploaded. */
const asExistingImage = (url?: string): GroupImageSelection =>
  url ? { uri: url, fileName: "", mimeType: "", isLocal: false } : undefined;

export const useGroupForm = (
  mode: "create" | "edit",
  initialValues: GroupEditProps,
  currentUser: UserProfile,
  callbacks?: {
    onSuccess?: () => void;
    onError?: (message?: string) => void;
  },
) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, onChangeName] = useState(initialValues.name);
  const [desc, onChangeDesc] = useState(initialValues.description);
  const [isPrivate, setIsPrivate] = useState(initialValues.isPrivate ?? false);

  // Two independent images. `isLocal` distinguishes a freshly picked file that
  // still needs uploading from a URL already stored on the group — the edit
  // form seeds both from the existing group so they render straight away and
  // survive a save that only touched the other one.
  const [selectedImage, setSelectedImage] = useState<GroupImageSelection>(
    asExistingImage(initialValues.coverImageUrl),
  );
  const [selectedProfileImage, setSelectedProfileImage] =
    useState<GroupImageSelection>(
      asExistingImage(initialValues.profileImageUrl),
    );

  const [membersModalVisible, setMembersModalVisible] = useState(false);
  const [invitedMembers, setInvitedMembers] = useState<InvitableMember[]>([]);

  const { mutateAsync: createGroup } = useCreateGroup();
  const { mutateAsync: updateGroup } = useUpdateGroup();
  const { mutateAsync: createGroupMembership } = useCreateGroupMembership();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: updateMemberCount } = useUpdateGroupMemberCount();
  const { mutate: sendNotification } = useSendNotification();

  const handleInvite = (members: InvitableMember[]) =>
    setInvitedMembers(members);
  const removeInvited = (id: string) =>
    setInvitedMembers((prev) => prev.filter((m) => m.itemId !== id));

  const handleSubmit = async () => {
    if (!name.trim()) {
      callbacks?.onError?.("ERROR_NAME_REQUIRED");
      return;
    }
    setIsSubmitting(true);
    try {
      const [coverImageUrl, profileImageUrl] = await Promise.all([
        resolveImageUrl(selectedImage),
        resolveImageUrl(selectedProfileImage),
      ]);
      if (mode === "create") {
        try {
          const data = await createGroup({
            name,
            description: desc,
            coverImageUrl,
            profileImageUrl,
            isPrivate,
          });

          const groupId = data.insertgroup.itemId;

          if (groupsFunctionEnabled) {
            // Server makes the creator (native CreatedBy) Admin and adds invitees.
            await callGroups({
              action: "initGroup",
              groupId,
              // UserProfile's declared type lags the runtime shape (see below).
              snapshot: currentUser as unknown as GroupSnapshot,
              members: invitedMembers.map((member) => ({
                userId: member.itemId,
                snapshot: {
                  firstName: member.firstName,
                  lastName: member.lastName,
                  email: member.email,
                  profileImageUrl: member.profileImageUrl,
                },
              })),
            });
          } else {
            await createGroupMembership({
              groupId,
              userId: currentUser.itemId,
              role: GroupMemberRole.ADMIN,
              status: MembershipStatus.ACTIVE,
              joinType: JoinType.DIRECT_JOIN,
              groupUserSnapshot: {
                firstName: currentUser.firstName,
                lastName: currentUser.lastName,
                email: currentUser.email,
                profileImageUrl: currentUser.profileImageUrl,
              },
              joinedAt: new Date().toLocaleDateString("en-GB"),
            });

            await Promise.all(
              invitedMembers.map((member) =>
                createGroupMembership({
                  groupId,
                  userId: member.itemId,
                  role: GroupMemberRole.MEMBER,
                  status: MembershipStatus.ACTIVE,
                  joinType: JoinType.ADDED_BY_ADMIN,
                  groupUserSnapshot: {
                    firstName: member.firstName,
                    lastName: member.lastName,
                    email: member.email,
                    profileImageUrl: member.profileImageUrl,
                  },
                  joinedAt: new Date().toLocaleDateString("en-GB"),
                }),
              ),
            );

          }

          // Notify each invited member they've been added to the group
          if (invitedMembers.length > 0) {
            sendNotification({
              userIds: invitedMembers.map((m) => m.itemId),
              denormalizedPayload: JSON.stringify({
                type: "added_to_group",
                message: t("NOTIFY_ADDED_TO_GROUP", {
                  firstName: currentUser.firstName,
                  lastName: currentUser.lastName,
                  groupName: name,
                }),
                actorAvatar: currentUser.profileImageUrl,
                actorName: `${currentUser.firstName} ${currentUser.lastName}`,
                actorId: currentUser.itemId,
                groupId,
                targetEntityId: groupId,
              }),
              configurationName: "add-to-group",
              responseKey: "group-action",
              responseValue: "added_to_group",
            });
          }

          await updateMemberCount(groupId);
          queryClient.invalidateQueries({ queryKey: ["getGroups"] });
          queryClient.invalidateQueries({ queryKey: ["getGroupById"] });
          // The creator was just made an Admin member above, so their membership
          // list — which gates group posts in the main feed — is now stale.
          queryClient.invalidateQueries({ queryKey: ["getUserGroupMemberships"] });
          callbacks?.onSuccess?.();
        } catch {
          callbacks?.onError?.();
        }
      } else {
        try {
          await updateGroup({
            name,
            description: desc,
            coverImageUrl,
            profileImageUrl,
            itemId: initialValues.ItemId,
            isPrivate: isPrivate,
          });
          queryClient.invalidateQueries({ queryKey: ["getGroups"] });
          queryClient.invalidateQueries({ queryKey: ["getGroupById"] });
          callbacks?.onSuccess?.();
        } catch {
          callbacks?.onError?.();
        } finally {
          setIsSubmitting(false);
        }
      }
      callbacks?.onSuccess?.();
    } catch {
      callbacks?.onError?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  // `pickSingleWithCrop` gives a crop step on both platforms — the built-in
  // editor on native, the overlay on web — where the previous
  // `launchImageLibraryAsync({ allowsEditing: true })` cropped on native only
  // and silently uploaded the raw file on web. That matters most for the round
  // avatar, which centre-crops whatever it is given.
  const pickImage = async (
    apply: (selection: NonNullable<GroupImageSelection>) => void,
  ) => {
    const asset = await pickSingleWithCrop();
    if (!asset) return;
    apply({
      uri: asset.uri,
      fileName: asset.fileName ?? `upload_${Date.now()}.jpg`,
      mimeType: asset.mimeType ?? "image/jpeg",
      isLocal: true,
    });
  };

  const pickImageAsync = () => pickImage(setSelectedImage);
  const pickProfileImageAsync = () => pickImage(setSelectedProfileImage);

  /** Uploads a newly picked file; passes an existing URL straight through. */
  const resolveImageUrl = async (
    selection: GroupImageSelection,
  ): Promise<string | undefined> => {
    if (!selection) return undefined;
    if (!selection.isLocal) return selection.uri;
    const uploadedUrl = await uploadFile({
      uri: selection.uri,
      fileName: selection.fileName,
      mimeType: selection.mimeType,
    });
    return uploadedUrl?.split("?")[0];
  };

  return {
    name,
    onChangeName,
    desc,
    onChangeDesc,
    selectedImage,
    setSelectedImage,
    selectedProfileImage,
    setSelectedProfileImage,
    membersModalVisible,
    setMembersModalVisible,
    invitedMembers,
    isPending: isSubmitting,
    handleInvite,
    removeInvited,
    pickImageAsync,
    pickProfileImageAsync,
    isPrivate,
    setIsPrivate,
    handleSubmit,
  };
};
